import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Shield, UserCog, User } from "lucide-react";
import { TIMEZONES } from "@/lib/timezones";
import { DatalistInput } from "./DatalistInput";

const ROLES = [
  { value: "employee", label: "Employee", desc: "Regular staff · mod-level metrics", icon: User, color: "border-slate-500 text-slate-200 bg-slate-500/10", active: "bg-slate-600/40 border-slate-400 text-white" },
  { value: "manager", label: "Manager", desc: "Team lead · admin-level metrics", icon: UserCog, color: "border-[#00e5ff]/40 text-[#00e5ff] bg-[#00e5ff]/8", active: "bg-[#00e5ff]/20 border-[#00e5ff] text-white" },
  { value: "admin", label: "Admin", desc: "Full access · owner-level metrics", icon: Shield, color: "border-[color:var(--accent)]/40 text-[color:var(--accent)] bg-[color:var(--accent)]/8", active: "bg-[color:var(--accent)]/25 border-[color:var(--accent)] text-white" },
];

export function InviteModal({ users, taxonomies, onClose, onSent }) {
  const [f, setF] = useState({ email: "", role: "employee", title: "", department: "", job: "", location: "", timezone: "", manager_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, manager_id: f.manager_id || null };
      await api.post("/invites", payload);
      toast.success(`Invitation sent to ${f.email}`);
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in" onClick={onClose}>
      <form
        onSubmit={submit}
        className="card-surface p-6 w-full max-w-lg fade-in-up max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-modal"
      >
        <div className="flex items-center gap-3 mb-1">
          <Mail size={20} className="text-[color:var(--accent)]" />
          <h2 className="text-xl font-bold">Send email invitation</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">They'll receive a branded email with a secure link to set their own password.</p>

        {/* PROMINENT ROLE PICKER */}
        <div className="mb-5 pb-4 border-b border-slate-800">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold mb-2">Role in the company</div>
          <div className="grid grid-cols-3 gap-2" data-testid="inv-role-picker">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = f.role === r.value;
              return (
                <button
                  type="button"
                  key={r.value}
                  onClick={() => set("role", r.value)}
                  data-testid={`inv-role-${r.value}`}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border-2 transition-all ${active ? r.active : `${r.color} hover:border-slate-400`}`}
                >
                  <Icon size={18} />
                  <div className="text-sm font-bold">{r.label}</div>
                  <div className="text-[9px] opacity-80 leading-tight text-center">{r.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="teammate@company.com" type="email" required data-testid="inv-email" className={`col-span-2 ${inputCls}`} />
          <select value={f.manager_id} onChange={(e) => set("manager_id", e.target.value)} data-testid="inv-manager" className={`col-span-2 ${inputCls}`}>
            <option value="">No manager</option>
            {users.filter((u) => u.role !== "employee").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Job title (optional)" data-testid="inv-title" className={inputCls} />
          <DatalistInput value={f.job} onChange={(v) => set("job", v)} options={taxonomies.job || []} placeholder="Job (optional)" testid="inv-job" className={inputCls} />
          <DatalistInput value={f.department} onChange={(v) => set("department", v)} options={taxonomies.department} placeholder="Department (optional)" testid="inv-department" className={inputCls} />
          <DatalistInput value={f.location} onChange={(v) => set("location", v)} options={taxonomies.location} placeholder="Location (optional)" testid="inv-location" className={inputCls} />
          <select value={f.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="inv-timezone" className={`col-span-2 ${inputCls}`}>
            <option value="">Timezone (optional)</option>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors">Cancel</button>
          <button type="submit" disabled={busy} data-testid="inv-submit" className="px-4 py-2 rounded-lg bg-[color:var(--accent)] text-white font-bold text-sm disabled:opacity-60">
            {busy ? "Sending…" : `Invite as ${ROLES.find((r) => r.value === f.role)?.label}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default InviteModal;
